import React, { useEffect, useRef, useState } from 'react';
import { FaSearchPlus, FaVolumeMute, FaVolumeUp } from 'react-icons/fa';
import WhatsAppButton from '../WhatsAppButton/WhatsAppButton';
import './AccessibilityControls.scss';

const LENS_SIZE = 200;
const ZOOM_SCALE = 2;
const SNAPSHOT_DEBOUNCE = 120;

const AccessibilityControls = () => {
  const [speechEnabled, setSpeechEnabled] = useState(false);
  const [magnifierEnabled, setMagnifierEnabled] = useState(false);
  const lensRef = useRef(null);
  const lastSpokenRef = useRef('');
  const captureTimeoutRef = useRef(null);

  const handleSpeechToggle = () => {
    if (!('speechSynthesis' in window)) {
      console.warn('Tarayici sesli okumayi desteklemiyor.');
      return;
    }
    setSpeechEnabled((prev) => !prev);
  };

  const handleMagnifierToggle = () => {
    setMagnifierEnabled((prev) => !prev);
  };

  useEffect(() => {
    if (!speechEnabled) {
      window.speechSynthesis?.cancel();
      return;
    }

    const handleHover = (event) => {
      const ariaLabel = event.target.getAttribute('aria-label');
      const alt = event.target.getAttribute('alt');
      const text = (ariaLabel || alt || event.target.innerText || '').trim().replace(/\s+/g, ' ');

      if (text.length < 3 || text === lastSpokenRef.current || text.length > 280) {
        return;
      }

      lastSpokenRef.current = text;
      const utterance = new SpeechSynthesisUtterance(text);

      // Configure voice settings
      utterance.lang = 'tr-TR'; // Turkish language
      utterance.rate = 0.9; // Slightly slower for clarity
      utterance.pitch = 1.3; // Higher pitch (temporary fix - install Microsoft Filiz for real female voice)

      // Get available voices
      const voices = window.speechSynthesis.getVoices();

      // Show available voices (for debugging)
      const turkishVoices = voices.filter(v => v.lang.startsWith('tr'));
      if (turkishVoices.length > 0) {
        console.log('Available Turkish voices:');
        turkishVoices.forEach((v, i) => {
          console.log(`${i + 1}. ${v.name} (${v.lang})`);
        });
      } else {
        console.log('No Turkish voices found. Available voices:', voices.map(v => v.name));
      }

      // Look for common female Turkish voice names
      const femaleVoiceNames = ['filiz', 'emel', 'female', 'kadın', 'woman'];
      const femaleTurkishVoice = voices.find(voice => {
        const voiceName = voice.name.toLowerCase();
        return voice.lang.startsWith('tr') &&
          femaleVoiceNames.some(name => voiceName.includes(name));
      });

      // Fallback to any Turkish voice
      const turkishVoice = voices.find(voice => voice.lang.startsWith('tr'));

      if (femaleTurkishVoice) {
        console.log('Using female voice:', femaleTurkishVoice.name);
        utterance.voice = femaleTurkishVoice;
      } else if (turkishVoice) {
        console.log('Using Turkish voice:', turkishVoice.name);
        utterance.voice = turkishVoice;
      }

      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utterance);
    };

    document.addEventListener('mouseover', handleHover);

    return () => {
      document.removeEventListener('mouseover', handleHover);
      window.speechSynthesis?.cancel();
      lastSpokenRef.current = '';
    };
  }, [speechEnabled]);

  useEffect(() => {
    const lens = lensRef.current;

    if (!magnifierEnabled) {
      if (lens) {
        lens.style.display = 'none';
        lens.style.backgroundImage = '';
      }
      return;
    }

    let currentSnapshot = null;
    let pendingSnapshot = null;
    let isCapturing = false;
    let lastPathname = window.location.pathname;

    // Take snapshot in background - doesn't block UI
    const takeSnapshotInBackground = async () => {
      if (isCapturing) return;
      isCapturing = true;

      try {
        const html2canvas = (await import('html2canvas')).default;

        const canvas = await html2canvas(document.body, {
          useCORS: true,
          allowTaint: true,
          backgroundColor: null,
          scale: 1,
          logging: false,
          ignoreElements: (el) => {
            return el === lens ||
              el.classList?.contains('magnifier-lens') ||
              el.classList?.contains('accessibility-floating');
          },
          onclone: (clonedDoc) => {
            // Remove box-shadows to prevent rendering artifacts (grey backgrounds, bad borders)
            const elements = clonedDoc.body.querySelectorAll('*');
            elements.forEach(el => {
              el.style.boxShadow = 'none';
            });
          }
        });

        // Store as pending first
        pendingSnapshot = {
          dataUrl: canvas.toDataURL('image/png'),
          width: canvas.width,
          height: canvas.height
        };

        // Preload image before applying
        const img = new Image();
        img.onload = () => {
          // Apply new snapshot only after image is loaded
          currentSnapshot = pendingSnapshot;
          if (lens && currentSnapshot) {
            lens.style.backgroundImage = `url(${currentSnapshot.dataUrl})`;
            lens.style.backgroundSize = `${currentSnapshot.width * ZOOM_SCALE}px ${currentSnapshot.height * ZOOM_SCALE}px`;
          }
        };
        img.src = pendingSnapshot.dataUrl;

      } catch (error) {
        console.error('Magnifier snapshot error:', error);
      } finally {
        isCapturing = false;
      }
    };

    const handleMove = (event) => {
      if (!lens || !currentSnapshot) return;

      const mouseX = event.clientX;
      const mouseY = event.clientY;

      lens.style.display = 'block';
      lens.style.left = `${mouseX - LENS_SIZE / 2}px`;
      lens.style.top = `${mouseY - LENS_SIZE / 2}px`;

      const pageX = event.pageX;
      const pageY = event.pageY;

      const bgPosX = -(pageX * ZOOM_SCALE - LENS_SIZE / 2);
      const bgPosY = -(pageY * ZOOM_SCALE - LENS_SIZE / 2);

      lens.style.backgroundPosition = `${bgPosX}px ${bgPosY}px`;
    };

    let lastMouseEvent = null;

    const updateLensOnScroll = () => {
      if (!lens || !currentSnapshot || !lastMouseEvent) return;

      const pageX = lastMouseEvent.pageX;
      const pageY = lastMouseEvent.pageY;

      const bgPosX = -(pageX * ZOOM_SCALE - LENS_SIZE / 2);
      const bgPosY = -(pageY * ZOOM_SCALE - LENS_SIZE / 2);

      lens.style.backgroundPosition = `${bgPosX}px ${bgPosY}px`;
    };

    const handleScroll = () => {
      requestAnimationFrame(updateLensOnScroll);
    };

    const handleMoveWithTracking = (event) => {
      lastMouseEvent = event;
      handleMove(event);
    };

    // Refresh snapshot on click (for menus, buttons, etc.)
    let clickDebounce = null;
    const handleClick = (event) => {
      // Ignore clicks on accessibility controls
      if (event.target.closest('.accessibility-floating')) return;

      // Debounce to prevent multiple rapid snapshots
      if (clickDebounce) clearTimeout(clickDebounce);
      clickDebounce = setTimeout(() => {
        takeSnapshotInBackground();
      }, 500); // Wait 500ms after click for DOM to settle
    };

    // Check for route changes
    const checkRouteChange = () => {
      const currentPathname = window.location.pathname;
      if (currentPathname !== lastPathname) {
        lastPathname = currentPathname;
        // Take new snapshot after route change - wait for data to load
        setTimeout(() => takeSnapshotInBackground(), 1000);
      }
    };

    const routeCheckInterval = setInterval(checkRouteChange, 200);

    // Initial snapshot - wait for page data to load
    setTimeout(() => takeSnapshotInBackground(), 1500);

    document.addEventListener('mousemove', handleMoveWithTracking);
    document.addEventListener('click', handleClick, true);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      document.removeEventListener('mousemove', handleMoveWithTracking);
      document.removeEventListener('click', handleClick, true);
      window.removeEventListener('scroll', handleScroll);
      clearInterval(routeCheckInterval);
      if (clickDebounce) clearTimeout(clickDebounce);
      if (captureTimeoutRef.current) clearTimeout(captureTimeoutRef.current);
      currentSnapshot = null;
      pendingSnapshot = null;
      if (lens) {
        lens.style.display = 'none';
        lens.style.backgroundImage = '';
      }
    };
  }, [magnifierEnabled]);

  return (
    <>
      <div className="accessibility-floating" aria-label="Erisilebilirlik kisayollari">
        <div className="accessibility-buttons">
          {/* <button
            type="button"
            className={`accessibility-button ${speechEnabled ? 'is-active' : ''}`}
            onClick={handleSpeechToggle}
            aria-pressed={speechEnabled}
            aria-label="Sesli Okuma"
          >
            {speechEnabled ? <FaVolumeUp /> : <FaVolumeMute />}
          </button>
          <button
            type="button"
            className={`accessibility-button ${magnifierEnabled ? 'is-active' : ''}`}
            onClick={handleMagnifierToggle}
            aria-pressed={magnifierEnabled}
            aria-label="Büyüteç"
          >
            <FaSearchPlus />
          </button> */}
          <WhatsAppButton phoneNumber="905305421939" />
        </div>
      </div>
      <div ref={lensRef} className="magnifier-lens" aria-hidden={!magnifierEnabled} />
    </>
  );
};

export default AccessibilityControls;
